/*
 * Ryan O'Dowd
 * 2024-09-27
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import Button from '@components/common/Button';
import CPassage from '@classes/Passage';
import Dialog from '@components/common/Dialog';
import LinearProgress from '@components/common/LinearProgress';
import React from 'react';
import Table from '@components/common/Table';
import Text from '@components/common/Text';
import Tooltip from '@components/common/Tooltip';
import {
  TouchableOpacity,
} from 'react-native';
import {
  format as dateFnsFormat,
} from 'date-fns';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const AssignmentProgressDialog = (props: AssignmentProgressDialogProps) => {
  const i18n = hooks.useI18n();
  const goalProgress = hooks.useTypedSelector((state) => state.goalProgress[props.goal.id]) || {};
  const classroomStudents = hooks.useTypedSelector((state) => state.classroomStudents[props.goal.classroom_goal_metadata.classroom_id] || []);

  const studentTableRows = Object.entries(goalProgress).map(([studentId, assignments]) => { // @TODO: this could probably be passed in from ClassroomGoal table data and filtered instead of calculating again
    const thisAssignmentProgress = assignments[props.assignment.id];
    const numCompleted = thisAssignmentProgress.nlc;
    const numLessonsRequired = thisAssignmentProgress.nlr;
    const progress = numCompleted / numLessonsRequired;
    const classroomStudent = classroomStudents.find((cs) => +cs.user_id === +studentId || +cs.user?.id === +studentId);

    let unit = props.assignment.assignment_object.must_memorize ? 'levels' : 'lessons';
    if (numLessonsRequired === 1) {
      unit = props.assignment.assignment_object.must_memorize ? 'level' : 'lesson';
    }

    return [ // @TODO: update ids
      {
        id: `${studentId}_display_name`,
        content: (
          <Tooltip title={classroomStudent?.user?.display_name}>
            <Text>{classroomStudent?.display_name || classroomStudent?.user?.display_name}</Text>
          </Tooltip>
        ),
        sortValue: classroomStudent?.display_name?.toLocaleLowerCase() || classroomStudent?.user?.display_name?.toLocaleLowerCase(),
      },
      {
        id: `${studentId}_date_started`,
        content: thisAssignmentProgress.ds ? <Tooltip title={dateFnsFormat(thisAssignmentProgress.ds, 'MMM d, yyyy, h:mm a')}><Text>{dateFnsFormat(thisAssignmentProgress.ds, 'MMM d')}</Text></Tooltip> : '-',
        sortValue: new Date(thisAssignmentProgress.ds || 0).getTime(),
      },
      {
        id: `${studentId}_date_completed`,
        content: thisAssignmentProgress.dc ? <Tooltip title={dateFnsFormat(thisAssignmentProgress.dc, 'MMM d, yyyy, h:mm a')}><Text>{dateFnsFormat(thisAssignmentProgress.dc, 'MMM d')}</Text></Tooltip> : '-',
        sortValue: new Date(thisAssignmentProgress.dc || 0).getTime(),
      },
      {
        id: `${studentId}_date_last_activity`,
        content: thisAssignmentProgress.dla ? <Tooltip title={dateFnsFormat(thisAssignmentProgress.dla, 'MMM d, yyyy, h:mm a')}><Text>{dateFnsFormat(thisAssignmentProgress.dla, 'MMM d')}</Text></Tooltip> : '-',
        sortValue: new Date(thisAssignmentProgress.dla || 0).getTime(),
      },
      {
        id: `${studentId}_total_milliseconds_spent`,
        content: !thisAssignmentProgress.t ? '-' : utilities.humanReadableTimeExact(thisAssignmentProgress.t), // @TODO: add sortBy attr to use userAssignmentProgress.t instead of the pretty time// @TODO: color code for more than one standard deviation away from mean
        sortValue: thisAssignmentProgress.t || 0,
      },
      {
        id: `${studentId}_progress`,
        content: (
          <Tooltip title={`${numCompleted} of ${numLessonsRequired} ${unit} completed`}>
            <TouchableOpacity onPress={() => {}}>{/* @TODO: go to user's progress table */}
              <LinearProgress progress={progress} />
            </TouchableOpacity>
          </Tooltip>
        ),
        sortValue: progress,
      },
    ];
  });

  return (
    <Dialog
      visible={true}
      onDismiss={() => props.onClose()} // @TODO: ask about unsaved changes
    >
      <Dialog.Title>
        {['verse_memory', 'reading'].includes(props.assignment.assignment_type)
          ? CPassage.createFromObject(props.assignment.assignment_object.passage).prettyPassageName()
          : props.assignment.name
        }
      </Dialog.Title>
      {['verse_memory', 'reading'].includes(props.assignment.assignment_type) &&
        <Dialog.Subtitle>{props.assignment.name}</Dialog.Subtitle>
      }
      <Dialog.Content>
        <Table// @TODO: combine with StudentAssignmentsDialog?
          tableTitle='Student progress'
          open={true}
          disableAccordion={true}
          headerRowData={[// @TODO: allow for sorting based on this
            {width: 200, id: 'name', content: 'Name'},
            {width: 100, id: 'date_started', content: 'Date started', rightAlign: true},
            {width: 100, id: 'date_completed', content: 'Date completed', rightAlign: true},
            {width: 100, id: 'last_activity', content: 'Last activity', rightAlign: true},
            {width: 100, id: 'time_spent', content: 'Time spent', rightAlign: true},
            // @TODO: total_num_questions_answered
            // @TODO: total_num_questions_skipped
            {width: 80, id: 'progress', content: 'Progress', rightAlign: true},
          ]}// @TODO: some of these should be named better and/or have tooltips
          isSortable={true}
          orderByIndex={3}
          rowData={studentTableRows}
          // @TODO: onRowPress={(row) => setSelectedStudentId(+row[0].id.split('_')[0])}// @TODO: way too hacky
          filtersJsx={null
            // @TODO: show/hide completed, etc.
          }
        />
      </Dialog.Content>
      <Dialog.Actions>
        <Button onPress={() => props.onClose()}>
          {i18n('close')}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

class AssignmentProgressDialogProps {
  onClose: () => void;
  assignment: any;
  goal: any;
};

export default AssignmentProgressDialog;
